import React, { useEffect, useMemo } from "react";
import { useUserAndGameContext } from "../../../contexts/UserAndGameContext";
import { useGolfLeagueContext } from "../context/GolfLeagueContext";
import { 
  Box, 
  Typography, 
  FormControl, 
  Select, 
  MenuItem,
} from "@mui/material";
import GolfOverallTable from "./GolfOverallTable";
import SimpleSelectionButtons from "../../GamePageComponents/TabSelectionButtons/SimpleSelectionButtons";
import { useSearchParams } from "react-router-dom";
import SelectionBar from "../../NavigationBars/MyPicksNavigation/SelectionBar";

const LeaderboardPage = () => {
    const {
      masterPoolId,
      gameId,
      gameAddress,
      page,
    } = useUserAndGameContext();

    const { 
      events, 
      teams, 
      players,
      intervalsToShow,
    } = useGolfLeagueContext();
    
    const [searchParams, setSearchParams] = useSearchParams();
    const sort = searchParams.get("sort") || "0"; // Default to "0" for team
    const paramEventId = searchParams.get("eventId");
    const filterTeamId = searchParams.get("teamId"); // Get teamId filter from URL
    
    // Create dropdown options for tournaments (without Overall for individual view)
    const eventOptions = useMemo(() => {
      // Create options from intervalsToShow
      return intervalsToShow?.map((interval) => {
        const event = events?.find((event) => event.interval === interval);
        return {
          value: interval,
          label: event ? event.name : `Interval ${interval}`,
          tournamentId: event?.tournamentId || String(interval),
        };
      }) || [];
    }, [intervalsToShow, events]);
    
    // Create dropdown options based on the current sort view
    const dropdownOptions = useMemo(() => {
      // Start with the event options (always needed)
      const options = [...eventOptions];
      
      // Add Overall option only when in team view
      if (sort === "0") {
        options.unshift({
          value: "overall",
          label: "Overall",
          tournamentId: "overall"
        });
      }
      
      return options;
    }, [eventOptions, sort]);
    
    // Convert dropdown options to the format needed for SelectionBar
    const selectionItems = useMemo(() => {
      return dropdownOptions?.map(option => ({
        eventId: option.tournamentId ? String(option.tournamentId) : "overall", // Use tournamentId as the ID
        name: option.label
      }));
    }, [dropdownOptions]);
    
    // Create team filter options for individual view
    const teamFilterOptions = useMemo(() => {
      if (!teams) return [];
      
      // Start with "All Players" option
      const options = [
        { teamId: "all", teamName: "All Players" }
      ];
      
      // Add all teams
      teams?.forEach(team => {
        if (team && team.teamId && team.teamName) {
          options.push({
            teamId: team.teamId,
            teamName: team.teamName,
            teamLogo: team.teamLogo
          });
        }
      });
      
      return options;
    }, [teams]);
    
    // Get a valid event ID for the current view
    const defaultEventId = useMemo(() => {
      if (sort === "0") {
        // For team view, default to "overall"
        return "overall";
      } else {
        // For individual view, default to first event 
        return eventOptions.length > 0 ? 
          (eventOptions[0].tournamentId || String(eventOptions[0].value)) : 
          null;
      }
    }, [sort, eventOptions]);
    
    // Determine which event to display
    const currentEventId = useMemo(() => {
      // If there's an eventId in the URL and it exists in the current dropdown options, use that
      if (paramEventId) {
        // Check if the paramEventId exists in the current dropdown options
        const exists = selectionItems.some(item => String(item.eventId) === String(paramEventId));
        if (exists) {
          return paramEventId;
        }
      }
      
      // Otherwise use the default for the current view
      return defaultEventId;
    }, [paramEventId, selectionItems, defaultEventId]);

    // Find the current dropdown option based on currentEventId
    const currentOption = useMemo(() => {
      return dropdownOptions.find(option => 
        String(option.tournamentId) === String(currentEventId)
      ) || dropdownOptions[0];
    }, [dropdownOptions, currentEventId]);

    // Create a mapping of teamId to team object for efficient lookup
    const teamMap = useMemo(() => {
      if (!teams) return {};
      
      const map = {};
      teams.forEach(team => {
        if (team && team.teamId) {
          map[team.teamId] = team;
        }
      });
      
      return map;
    }, [teams]);

    // Handle tab change (switching between team and individual standings)
    const handleTabChange = (sortValue) => {
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.set("sort", sortValue);
        
        // Logic for event selection when switching tabs
        if (sortValue === "1" && (paramEventId === "overall" || !paramEventId)) {
            // Switching to individual view and current selection is "overall" or none
            // Select the first event in the list
            const firstEventId = eventOptions.length > 0 ? 
                (eventOptions[0].tournamentId || String(eventOptions[0].value)) : 
                null;
                
            if (firstEventId) {
                newSearchParams.set("eventId", String(firstEventId));
            }
        } else if (sortValue === "0" && !paramEventId) {
            // Switching to team view with no selection, set to overall
            newSearchParams.set("eventId", "overall");
        }
        
        // Remove teamId when switching to team view
        if (sortValue === "0" && newSearchParams.has("teamId")) {
            newSearchParams.delete("teamId");
        }
        
        setSearchParams(newSearchParams);
    };

    // Handle event selection change
    const handleEventChange = (newEventId) => {
        //console.log("Event changed to:", newEventId);
        
        // Find the dropdown option with this tournamentId
        const selectedOption = dropdownOptions.find(
          option => String(option.tournamentId) === String(newEventId)
        );
        
    };
    
    // Handle team filter change in individual view
    const handleTeamFilterChange = (event) => {
        const newTeamId = event.target.value;
        const newSearchParams = new URLSearchParams(searchParams);
        
        if (newTeamId === "all") {
            // If "All Players" selected, remove teamId from URL
            newSearchParams.delete("teamId");
        } else {
            // Otherwise set the teamId
            newSearchParams.set("teamId", newTeamId);
        }
        
        setSearchParams(newSearchParams);
    };
    
    // Effect to ensure URL has valid parameters when component mounts or when sort changes
    useEffect(() => {
        const newSearchParams = new URLSearchParams(searchParams);
        let needsUpdate = false;
        
        // Check if eventId param is valid for current view
        if (sort === "1" && paramEventId === "overall") {
            // Individual view doesn't support "overall", change to first event
            const firstEventId = eventOptions.length > 0 ? 
                (eventOptions[0].tournamentId || String(eventOptions[0].value)) : 
                null;
                
            if (firstEventId) {
                newSearchParams.set("eventId", String(firstEventId));
                needsUpdate = true;
            }
        } else if (!paramEventId) {
            // No event selected, set default
            newSearchParams.set("eventId", defaultEventId);
            needsUpdate = true;
        }
        
        // If on team view and teamId is set, remove it
        if (sort === "0" && newSearchParams.has("teamId")) {
            newSearchParams.delete("teamId");
            needsUpdate = true;
        }
        
        // Update URL if needed
        if (needsUpdate) {
            setSearchParams(newSearchParams);
        }
    }, [sort, paramEventId, defaultEventId, eventOptions]);
    
    // Map your text labels for the sorts
    const tabTexts = ["Team Standings", "Individual Standings"];

    const legendData = [
        "MC - Missed Cut",
        "WD - Withdrawn",
        "DQ - Disqualified",
        "NA - Did Not Play"
    ];

    // Team data mapping for sort === "0"
    const teamsData = useMemo(() => {
      if (!teams) return [];
      
      return teams.map((team) => {
          const teamId = team?.teamId;
          const teamName = team?.teamName;
          const teamLogo = team?.teamLogo;
          
          // Get the appropriate score data based on the current event ID
          let scoreData = null;
          
          if (currentEventId === "overall") {
              // Use overall scores
              scoreData = team?.scores?.overall;
          } else {
              // Try to find scores for the specific tournament
              scoreData = team?.scores?.[currentEventId];
              
              // If no scores for this specific tournament, fall back to empty object
              if (!scoreData) {
                  scoreData = {};
              }
          }
          
          // Format score for display
          const formattedScore = scoreData?.score !== undefined ? 
              (scoreData.score === 0 ? 'E' : 
               scoreData.score > 0 ? `+${scoreData.score}` : 
               scoreData.score.toString()) : '-';
          
          // Format combined score/rank display
          const rankDisplay = scoreData?.rank ? 
              `${scoreData.tied ? 'T-' : ''}${scoreData.rank}` : '-';
              
          return {
              id: teamId,
              rank: scoreData?.rank || '-', // Default display
              name: teamName,
              logoUrl: teamLogo,
              total: formattedScore,
              strokes: scoreData?.round1 && scoreData?.round2 ? 
                  (scoreData.round1 + scoreData.round2).toString() : 
                  scoreData?.round1 ? 
                      scoreData.round1.toString() : '-',
              round1: scoreData?.round1?.toString() || '-',
              round2: scoreData?.round2?.toString() || '-',
              rankDisplay: rankDisplay,
              tied: scoreData?.tied || false
          };
      }).sort((a, b) => {
          // Sort by rank, handle non-numeric ranks
          const rankA = typeof a.rank === 'number' ? a.rank : 999;
          const rankB = typeof b.rank === 'number' ? b.rank : 999;
          return rankA - rankB;
      });
    }, [teams, currentEventId]);

    // Player data mapping for sort === "1", with team filtering
    const playersData = useMemo(() => {
      if (!players || !teamMap) return [];
      
      return players
        // Apply team filter if set
        .filter(player => {
          if (!filterTeamId || filterTeamId === "all") {
            return true; // Show all players
          }
          
          // Filter to only players on the selected team
          return String(player.teamId) === String(filterTeamId);
        })
        .map((player) => {
          const playerId = player?.playerId;
          const playerName = `${player?.firstName} ${player?.lastName}`;
          const teamId = player?.teamId;
          
          // Look up the team for this player
          const playerTeam = teamMap[teamId];
          const teamLogo = playerTeam?.teamLogo || '';
          const teamName = playerTeam?.teamName || '';
          
          // Get the appropriate score data based on the current event ID
          let scoreData = null;
          
          // Players don't have overall scores, so we only check for the specific tournament
          if (currentEventId !== "overall" && player?.scores) {
              scoreData = player.scores[currentEventId];
              
              // If no scores for this specific tournament, fall back to empty object
              if (!scoreData) {
                  scoreData = {};
              }
          }
          
          // Format score for display
          const formattedScore = scoreData?.score !== undefined ? 
              (scoreData.score === 0 ? 'E' : 
               scoreData.score > 0 ? `+${scoreData.score}` : 
               scoreData.score.toString()) : '-';
          
          // Status text (MC, WD, DQ, etc.)
          const status = scoreData?.status || '';
          
          // Format combined score/rank display
          const rankDisplay = scoreData?.rank ? 
              `${scoreData.tied ? 'T-' : ''}${scoreData.rank}` : 
              status ? status : '-';
              
          return {
              id: playerId,
              rank: scoreData?.rank || '-', // Default display
              name: playerName,
              teamId: teamId,
              teamName: teamName,
              logoUrl: teamLogo,
              total: status || formattedScore,
              strokes: status !== '' ?
                '-' :
                scoreData?.round1 && scoreData?.round2 ? 
                    (scoreData.round1 + scoreData.round2).toString() : 
                    scoreData?.round1 ? 
                        scoreData.round1.toString() : '-',
              round1: scoreData?.round1?.toString() || '-',
              round2: scoreData?.round2?.toString() || '-',
              rankDisplay: rankDisplay,
              tied: scoreData?.tied || false,
              status: status
          };
      }).sort((a, b) => {
        // First, prioritize players with numerical ranks
        if (typeof a.rank === 'number' && typeof b.rank !== 'number') return -1;
        if (typeof a.rank !== 'number' && typeof b.rank === 'number') return 1;
        
        // If both have numerical ranks, sort by rank value
        if (typeof a.rank === 'number' && typeof b.rank === 'number') {
          return a.rank - b.rank;
        }
        
        // Define status priority order (lower number = higher priority)
        const getStatusPriority = (status) => {
          switch(status) {
            case 'MC': return 1;
            case 'WD': return 2;
            case 'DQ': return 3;
            case 'NA': return 4;
            default: return 0; // No status or unknown status
          }
        };
        
        const priorityA = getStatusPriority(a.status);
        const priorityB = getStatusPriority(b.status);
        
        // If one has status and other doesn't
        if (priorityA > 0 && priorityB === 0) return 1;
        if (priorityA === 0 && priorityB > 0) return -1;
        
        // If both have status, sort by the defined priority
        if (priorityA > 0 && priorityB > 0) {
          return priorityA - priorityB;
        }
        
        // For any other case, maintain the original order
        return 0;
    });
  }, [players, teamMap, currentEventId, filterTeamId]);

    // Choose the correct data based on the current sort
    const displayData = sort === "0" ? teamsData : playersData;
    
    // Get the current team filter name
    const currentTeamFilter = useMemo(() => {
        if (!filterTeamId || filterTeamId === "all") {
            return "All Players";
        }
        
        const team = teamMap[filterTeamId];
        return team ? team.teamName : "All Players";
    }, [filterTeamId, teamMap]);
    
    return (
      <Box>
          {/* Sort selection buttons */}
          <SimpleSelectionButtons
              selectedTab={sort}  
              tabTexts={tabTexts}
              customHandler={handleTabChange}
              paramName="sort"
          />
          
          {/* Event selection bar */}
          {selectionItems.length > 0 && (
              <SelectionBar
                  selectedId={currentEventId}
                  items={selectionItems}
                  idKey="eventId"
                  nameKey="name"
                  urlParamKey="eventId"
                  onChange={handleEventChange}
                  backgroundColor="#07356B"
                  textColor="#ffffff"
              />
          )}

          {/* Team filter dropdown for individual standings */}
          {sort === "1" && (
              <Box sx={{ 
                  width: '90%', 
                  maxWidth: '1000px', // Match the table width
                  margin: '20px auto 10px auto',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center' // Center the filter UI
              }}>
                  <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      maxWidth: '400px' // Constrains the maximum width
                  }}>
                      <Typography 
                          sx={{ 
                              fontSize: '16px', 
                              fontWeight: '700', 
                              marginRight: '16px',
                              color: '#002129'
                          }}
                      >
                          TEAM:
                      </Typography>
                      <FormControl 
                          variant="outlined" 
                          size="small"
                          sx={{ 
                              minWidth: 200,
                              '& .MuiOutlinedInput-root': {
                                  '& fieldset': {
                                      borderColor: '#07356B',
                                  },
                                  '&:hover fieldset': {
                                      borderColor: '#07356B',
                                  },
                                  '&.Mui-focused fieldset': {
                                      borderColor: '#07356B',
                                  },
                              },
                          }}
                      >
                          <Select
                              value={filterTeamId || "all"}
                              onChange={handleTeamFilterChange}
                              displayEmpty
                              sx={{
                                  backgroundColor: '#fff',
                                  fontSize: '14px',
                                  fontWeight: 600,
                                  color: '#002129',
                              }}
                          >
                              {teamFilterOptions.map((option) => (
                                  <MenuItem 
                                      key={option.teamId} 
                                      value={option.teamId}
                                      sx={{
                                          display: 'flex',
                                          alignItems: 'center', // Center items vertically
                                          gap: '8px',
                                          padding: '6px 16px',
                                          fontSize: '14px'
                                      }}
                                  >
                                      <Box sx={{ 
                                          display: 'flex',
                                          alignItems: 'center',
                                          height: '24px', // Fixed height for consistency
                                      }}>
                                          {option.teamId !== "all" && option.teamLogo && (
                                              <img 
                                                  src={option.teamLogo} 
                                                  alt={option.teamName}
                                                  style={{ 
                                                      width: '24px', 
                                                      height: '24px',
                                                      objectFit: 'contain',
                                                      marginRight: '8px',
                                                      display: 'block' // Ensures consistent layout
                                                  }}
                                              />
                                          )}
                                          {option.teamId === "all" && (
                                              <Box sx={{ 
                                                  width: '24px', 
                                                  height: '24px',
                                                  marginRight: '8px' 
                                              }} />
                                          )}
                                          {option.teamName}
                                      </Box>
                                  </MenuItem>
                              ))}
                          </Select>
                      </FormControl>
                  </Box>
              </Box>
          )}

          {/* Leaderboard table */}
          <GolfOverallTable 
              scoreData={displayData} 
              label={sort === "0" ? "TEAM" : "PLAYER"} 
              idToSet={sort === "0" ? "teamId" : "playerId"}
              teams={teams}
              players={players}
              events={events}
          />
          
          {/* Legend for statuses */}
          {sort === "1" && (
            <Box
                sx={{
                    width: '80%',
                    maxWidth: '600px',
                    margin: '0 auto',
                    padding: '16px 0px 32px 0px',
                }}
            >
                {legendData?.map((item, index) => (
                    <Typography key={index} variant="body2" sx={{ margin: '4px 0', fontSize: '14px', color: '#002129', fontWeight: '700' }}>
                        {item}
                    </Typography>
                ))}
            </Box>
          )}
      </Box>
  );
}

export default LeaderboardPage;